import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Home, FileText, Calendar, User, Briefcase, Phone, Save, X, Edit, CreditCard } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [workData, setWorkData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    workName: '',
    clientName: '',
    clientPhone: '',
    date: '',
    description: ''
  });

  useEffect(() => {
    // Get work data from navigation state
    if (location.state?.workData) {
      setWorkData(location.state.workData);
      setEditData({
        workName: location.state.workData.workName || '',
        clientName: location.state.workData.clientName || '',
        clientPhone: location.state.workData.clientPhone || '',
        date: location.state.workData.date || '',
        description: location.state.workData.description || ''
      });
    } else {
      // If no data, redirect back to work page
      navigate('/work');
    }
  }, [location, navigate]);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset edit data to original
    setEditData({
      workName: workData.workName || '',
      clientName: workData.clientName || '',
      clientPhone: workData.clientPhone || '',
      date: workData.date || '',
      description: workData.description || ''
    });
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_URL}/api/work/${workData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const updatedWork = await response.json();
        setWorkData(updatedWork);
        setIsEditing(false);
        toast({
          title: 'הצלחה',
          description: 'פרטי העבודה עודכנו בהצלחה'
        });
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating work:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לעדכן פרטי עבודה',
        variant: 'destructive'
      });
    }
  };

  if (!workData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" dir="rtl">
      <div className="container mx-auto px-4 py-16">
        {/* Header with Action Buttons */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800">פרטי עבודה</h1>
          <div className="flex gap-4">
            <Button
              onClick={() => navigate('/price-list', { state: { workId: workData.id, workName: workData.workName } })}
              className="px-8 py-6 text-lg font-semibold bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              size="lg"
            >
              <FileText className="ml-2 w-6 h-6" />
              מחירון
            </Button>
            <Button
              onClick={() => navigate('/work-detail-list', { state: { workId: workData.id, workName: workData.workName } })}
              className="px-8 py-6 text-lg font-semibold bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              size="lg"
            >
              <FileText className="ml-2 w-6 h-6" />
              פירוט עבודה
            </Button>
          </div>
        </div>

        {/* Work Information Card */}
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-xl">
            <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
              <div className="flex justify-between items-center">
                <CardTitle className="text-2xl">מידע על העבודה</CardTitle>
                {!isEditing && (
                  <div className="text-sm opacity-80 flex items-center gap-2">
                    <Edit className="w-4 h-4" />
                    לחץ פעמיים לעריכה
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-8">
              {isEditing ? (
                /* Edit Mode */
                <div className="space-y-6">
                  {/* Work Name */}
                  <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                    <Briefcase className="w-6 h-6 text-blue-600 mt-3" />
                    <div className="flex-1">
                      <div className="text-sm text-gray-600 mb-2">שם עבודה</div>
                      <Input
                        value={editData.workName}
                        onChange={(e) => setEditData({ ...editData, workName: e.target.value })}
                        className="text-lg font-semibold"
                      />
                    </div>
                  </div>

                  {/* Client Name */}
                  <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-lg">
                    <User className="w-6 h-6 text-purple-600 mt-3" />
                    <div className="flex-1">
                      <div className="text-sm text-gray-600 mb-2">שם מזמין</div>
                      <Input
                        value={editData.clientName}
                        onChange={(e) => setEditData({ ...editData, clientName: e.target.value })}
                        className="text-lg font-semibold"
                      />
                    </div>
                  </div>

                  {/* Client Phone */}
                  <div className="flex items-start gap-4 p-4 bg-pink-50 rounded-lg">
                    <Phone className="w-6 h-6 text-pink-600 mt-3" />
                    <div className="flex-1">
                      <div className="text-sm text-gray-600 mb-2">מספר טלפון</div>
                      <Input
                        value={editData.clientPhone}
                        onChange={(e) => setEditData({ ...editData, clientPhone: e.target.value })}
                        className="text-lg font-semibold"
                        type="tel"
                        dir="ltr"
                      />
                    </div>
                  </div>

                  {/* Date */}
                  <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg">
                    <Calendar className="w-6 h-6 text-green-600 mt-3" />
                    <div className="flex-1">
                      <div className="text-sm text-gray-600 mb-2">תאריך</div>
                      <Input
                        value={editData.date}
                        onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                        type="date"
                        className="text-lg font-semibold"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-sm text-gray-600 mb-2">תיאור</div>
                    <Textarea
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      className="text-lg"
                      rows={4}
                    />
                  </div>

                  {/* Save/Cancel Buttons */}
                  <div className="flex gap-4 justify-end pt-4">
                    <Button
                      onClick={handleCancel}
                      variant="outline"
                      className="px-6"
                    >
                      <X className="ml-2 w-5 h-5" />
                      ביטול
                    </Button>
                    <Button
                      onClick={handleSave}
                      className="px-6 bg-green-600 hover:bg-green-700"
                    >
                      <Save className="ml-2 w-5 h-5" />
                      שמור
                    </Button>
                  </div>
                </div>
              ) : (
                /* View Mode - Double click to edit */
                <div 
                  className="space-y-6 cursor-pointer" 
                  onDoubleClick={handleDoubleClick}
                  title="לחץ פעמיים לעריכה"
                >
                  {/* Work Name */}
                  <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                    <Briefcase className="w-6 h-6 text-blue-600 mt-1" />
                    <div className="flex-1">
                      <div className="text-sm text-gray-600 mb-1">שם עבודה</div>
                      <div className="text-xl font-bold text-gray-800">{workData.workName}</div>
                    </div>
                  </div>

                  {/* Client Name */}
                  <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                    <User className="w-6 h-6 text-purple-600 mt-1" />
                    <div className="flex-1">
                      <div className="text-sm text-gray-600 mb-1">שם מזמין</div>
                      <div className="text-xl font-bold text-gray-800">{workData.clientName}</div>
                    </div>
                  </div>

                  {/* Client Phone */}
                  {workData.clientPhone && (
                    <div className="flex items-start gap-4 p-4 bg-pink-50 rounded-lg hover:bg-pink-100 transition-colors">
                      <Phone className="w-6 h-6 text-pink-600 mt-1" />
                      <div className="flex-1">
                        <div className="text-sm text-gray-600 mb-1">מספר טלפון</div>
                        <div className="text-xl font-bold text-gray-800 direction-ltr">
                          <a href={`tel:${workData.clientPhone}`} className="hover:text-pink-600 transition-colors">
                            {workData.clientPhone}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Date */}
                  <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                    <Calendar className="w-6 h-6 text-green-600 mt-1" />
                    <div className="flex-1">
                      <div className="text-sm text-gray-600 mb-1">תאריך</div>
                      <div className="text-xl font-bold text-gray-800">
                        {new Date(workData.date).toLocaleDateString('he-IL', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {workData.description && (
                    <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="text-sm text-gray-600 mb-2">תיאור</div>
                      <div className="text-lg text-gray-800 whitespace-pre-wrap">{workData.description}</div>
                    </div>
                  )}

                  {/* Payments Button */}
                  <div className="pt-4 border-t">
                    <Button
                      onClick={() => navigate(`/work-payments?workId=${workData.id}`)}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white py-6 text-lg"
                    >
                      <CreditCard className="ml-2 w-6 h-6" />
                      תשלומים
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WorkDetails;
